/**
 * CookieConsent - GDPR & Google Consent Mode v2 compliant cookie banner
 * 
 * Implements:
 * - Google Consent Mode v2 (ad_storage, ad_user_data, ad_personalization, analytics_storage)
 * - Granular consent options
 * - Persistent consent storage
 * - Third-party cookie disclosure (GetYourGuide, Stripe, Plausible)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import './CookieConsent.css';

// Consent categories
export interface CookieConsentState {
    necessary: boolean; // Always true, cannot be disabled
    analytics: boolean;
    marketing: boolean;
    functionality: boolean;
    timestamp: number;
    version: string;
}

const CONSENT_VERSION = '1.0';
const CONSENT_KEY = 'raisa_cookie_consent';

// Default consent state (before user interaction)
const DEFAULT_CONSENT: CookieConsentState = {
    necessary: true,
    analytics: false,
    marketing: false,
    functionality: false,
    timestamp: 0,
    version: CONSENT_VERSION,
};

// Get stored consent
export function getStoredConsent(): CookieConsentState | null {
    try {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (!stored) return null;
        
        const consent = JSON.parse(stored) as CookieConsentState;
        
        // Check if consent version matches
        if (consent.version !== CONSENT_VERSION) {
            return null; // Force re-consent on version change
        }
        
        return consent;
    } catch {
        return null;
    }
}

// Save consent
function saveConsent(consent: CookieConsentState): void {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

// Update Google Consent Mode
function updateGoogleConsent(consent: CookieConsentState): void {
    if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
            'ad_storage': consent.marketing ? 'granted' : 'denied',
            'ad_user_data': consent.marketing ? 'granted' : 'denied',
            'ad_personalization': consent.marketing ? 'granted' : 'denied',
            'analytics_storage': consent.analytics ? 'granted' : 'denied',
            'functionality_storage': consent.functionality ? 'granted' : 'denied',
            'personalization_storage': consent.functionality ? 'granted' : 'denied',
            'security_storage': 'granted', // Always granted for security
        });
    }
}

// Initialize default consent (called before gtag loads)
export function initializeDefaultConsent(): void {
    const stored = getStoredConsent();
    
    if (typeof window !== 'undefined') {
        // Define dataLayer and gtag function
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(...args: any[]) {
            (window as any).dataLayer.push(args);
        }
        (window as any).gtag = gtag;
        
        // Set default consent state
        gtag('consent', 'default', {
            'ad_storage': stored?.marketing ? 'granted' : 'denied',
            'ad_user_data': stored?.marketing ? 'granted' : 'denied',
            'ad_personalization': stored?.marketing ? 'granted' : 'denied',
            'analytics_storage': stored?.analytics ? 'granted' : 'denied',
            'functionality_storage': stored?.functionality ? 'granted' : 'denied',
            'personalization_storage': stored?.functionality ? 'granted' : 'denied',
            'security_storage': 'granted',
            'wait_for_update': 500, // Wait 500ms for consent update
        });
    }
}

interface CookieConsentProps {
    onConsentChange?: (consent: CookieConsentState) => void;
}

export default function CookieConsent({ onConsentChange }: CookieConsentProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [consent, setConsent] = useState<CookieConsentState>(DEFAULT_CONSENT);

    const getLocalizedPath = (path: string) => {
        const lang = i18n.language.split('-')[0];
        if (lang === 'en') return path;
        return `/${lang}${path}`;
    };

    const handleLearnMore = () => {
        navigate({ to: getLocalizedPath('/cookies') as any });
    };

    // Check if consent is needed on mount
    useEffect(() => {
        const stored = getStoredConsent();
        if (!stored) {
            setIsVisible(true);
        } else {
            setConsent(stored);
            updateGoogleConsent(stored);
        }
    }, []);

    const handleAcceptAll = () => {
        const newConsent: CookieConsentState = {
            necessary: true,
            analytics: true,
            marketing: true,
            functionality: true,
            timestamp: Date.now(),
            version: CONSENT_VERSION,
        };
        
        saveConsent(newConsent);
        updateGoogleConsent(newConsent);
        setConsent(newConsent);
        setIsVisible(false);
        onConsentChange?.(newConsent);
    };

    const handleRejectAll = () => {
        const newConsent: CookieConsentState = {
            necessary: true,
            analytics: false,
            marketing: false,
            functionality: false,
            timestamp: Date.now(),
            version: CONSENT_VERSION,
        };
        
        saveConsent(newConsent);
        updateGoogleConsent(newConsent);
        setConsent(newConsent);
        setIsVisible(false);
        onConsentChange?.(newConsent);
    };

    const handleSavePreferences = () => {
        const newConsent: CookieConsentState = {
            ...consent,
            timestamp: Date.now(),
            version: CONSENT_VERSION,
        };
        
        saveConsent(newConsent);
        updateGoogleConsent(newConsent);
        setIsVisible(false);
        onConsentChange?.(newConsent);
    };

    const toggleCategory = (category: keyof CookieConsentState) => {
        if (category === 'necessary') return; // Cannot disable necessary cookies
        setConsent(prev => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    if (!isVisible) return null;

    return (
        <div className="cookie-consent-overlay">
            <div className="cookie-consent-banner">
                <div className="cookie-consent-content">
                    <div className="cookie-header">
                        <span className="cookie-icon">🍪</span>
                        <h3>{t('cookies.title')}</h3>
                    </div>
                    
                    <p className="cookie-description">
                        {t('cookies.description')}{' '}
                        <button type="button" onClick={handleLearnMore} className="cookie-link">
                            {t('cookies.learn_more')}
                        </button>
                    </p>

                    {showDetails && (
                        <div className="cookie-categories">
                            {/* Necessary - Always on */}
                            <div className="cookie-category">
                                <div className="category-header">
                                    <label className="category-toggle">
                                        <input
                                            type="checkbox"
                                            checked={true}
                                            disabled
                                        />
                                        <span className="toggle-slider disabled"></span>
                                    </label>
                                    <div className="category-info">
                                        <strong>{t('cookies.necessary_title')}</strong>
                                        <span className="category-badge">{t('cookies.always_active')}</span>
                                    </div>
                                </div>
                                <p className="category-description">{t('cookies.necessary_desc')}</p>
                            </div>

                            {/* Analytics */}
                            <div className="cookie-category">
                                <div className="category-header">
                                    <label className="category-toggle">
                                        <input
                                            type="checkbox"
                                            checked={consent.analytics}
                                            onChange={() => toggleCategory('analytics')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <div className="category-info">
                                        <strong>{t('cookies.analytics_title')}</strong>
                                    </div>
                                </div>
                                <p className="category-description">{t('cookies.analytics_desc')}</p>
                            </div>

                            {/* Marketing */}
                            <div className="cookie-category">
                                <div className="category-header">
                                    <label className="category-toggle">
                                        <input
                                            type="checkbox"
                                            checked={consent.marketing}
                                            onChange={() => toggleCategory('marketing')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <div className="category-info">
                                        <strong>{t('cookies.marketing_title')}</strong>
                                    </div>
                                </div>
                                <p className="category-description">{t('cookies.marketing_desc')}</p>
                            </div>

                            {/* Functionality */}
                            <div className="cookie-category">
                                <div className="category-header">
                                    <label className="category-toggle">
                                        <input
                                            type="checkbox"
                                            checked={consent.functionality}
                                            onChange={() => toggleCategory('functionality')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <div className="category-info">
                                        <strong>{t('cookies.functionality_title')}</strong>
                                    </div>
                                </div>
                                <p className="category-description">{t('cookies.functionality_desc')}</p>
                            </div>
                        </div>
                    )}

                    <div className="cookie-actions">
                        {showDetails ? (
                            <>
                                <button 
                                    className="cookie-btn secondary"
                                    onClick={() => setShowDetails(false)}
                                >
                                    {t('cookies.back')}
                                </button>
                                <button 
                                    className="cookie-btn primary"
                                    onClick={handleSavePreferences}
                                >
                                    {t('cookies.save_preferences')}
                                </button>
                            </>
                        ) : (
                            <>
                                <button 
                                    className="cookie-btn secondary"
                                    onClick={handleRejectAll}
                                >
                                    {t('cookies.reject_all')}
                                </button>
                                <button 
                                    className="cookie-btn tertiary"
                                    onClick={() => setShowDetails(true)}
                                >
                                    {t('cookies.customize')}
                                </button>
                                <button 
                                    className="cookie-btn primary"
                                    onClick={handleAcceptAll}
                                >
                                    {t('cookies.accept_all')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
